import { fetchAuditLog } from "../lib/api.js";
import { escapeHtml, showError } from "../lib/ui.js";

const TABLE_LABELS = {
  events: "Evento",
  event_finance: "Números del evento",
  movements: "Movimiento",
  profiles: "Persona",
  enrollments: "Inscripción",
  package_rates: "Tarifas de paquete",
  // Colonia (0020)
  seasons: "Temporada",
  app_settings: "Interruptor",
  colonia_turnos: "Turno de colonia",
  colonia_plazos: "Plazo de colonia",
  colonia_canales: "Canal de origen",
  colonia_cuentas: "Cuenta de cobro",
  colonia_colegios: "Colegio",
  colonia_tarifas: "Tarifa de colonia",
  colonia_beneficios: "Beneficio exclusivo",
  colonia_responsables: "Responsable (colonia)",
  colonia_chicos: "Chico (colonia)",
  colonia_items: "Modalidad (colonia)",
  colonia_cuotas: "Cuota (colonia)",
  colonia_cobros: "Cobro (colonia)",
  // Tanda 2 (0021)
  colonia_staff_categorias: "Categoría de staff",
  colonia_staff: "Equipo (colonia)",
  colonia_gastos: "Gasto (colonia)",
  empresa_categorias: "Categoría de estructura",
  empresa_estructura: "Estructura fija",
};

const ACTION_LABELS = { insert: "cargó", update: "modificó", delete: "borró" };

/** Campos que no aportan nada al leer un cambio. */
const NOISE = new Set(["updated_at", "created_at", "id", "event_id"]);

export async function renderAudit(container) {
  container.innerHTML = `<h2>Historial de cambios</h2><p>Cargando…</p>`;

  try {
    const entries = await fetchAuditLog({ limit: 200 });
    let filterText = "";

    const draw = () => {
      const visible = entries.filter((e) => {
        if (!filterText) return true;
        const haystack = `${e.actor_name ?? ""} ${TABLE_LABELS[e.table_name] ?? e.table_name} ${describe(e)}`;
        return haystack.toLowerCase().includes(filterText.toLowerCase());
      });

      container.innerHTML = `
        <div id="view">
          <h2>Historial de cambios</h2>
          <p class="hint">
            Quién cargó o modificó qué, y cuándo. Los movimientos que genera el sistema solo
            a partir de un evento no se listan acá: quedan trazados por el evento que los originó.
          </p>

          <div class="filters">
            <input type="search" id="f-audit" placeholder="Buscar por persona o tipo…" value="${escapeHtml(filterText)}" />
          </div>

          <div class="panel">
            ${
              visible.length === 0
                ? "<p>No hay cambios registrados todavía.</p>"
                : `<div class="table-wrap"><table class="table-cards">
                    <thead><tr><th>Cuándo</th><th>Quién</th><th>Qué</th><th>Detalle</th></tr></thead>
                    <tbody>
                      ${visible
                        .map(
                          (e) => `<tr>
                            <td data-label="Cuándo">${formatTimestamp(e.at)}</td>
                            <td data-label="Quién">${escapeHtml(e.actor_name ?? "sistema")}</td>
                            <td data-label="Qué">${escapeHtml(ACTION_LABELS[e.action])} ${escapeHtml(TABLE_LABELS[e.table_name] ?? e.table_name)}</td>
                            <td data-label="Detalle">${escapeHtml(describe(e))}</td>
                          </tr>`
                        )
                        .join("")}
                    </tbody>
                  </table></div>`
            }
          </div>
        </div>
      `;

      const input = container.querySelector("#f-audit");
      input.addEventListener("input", () => {
        filterText = input.value;
        draw();
        const again = container.querySelector("#f-audit");
        again.focus();
        again.setSelectionRange(again.value.length, again.value.length);
      });
    };

    draw();
  } catch (error) {
    showError(container, error);
  }
}

/** Resume un cambio en una línea legible: sólo los campos que cambiaron. */
function describe(entry) {
  if (entry.action === "insert") {
    return entry.after?.description ?? entry.after?.child_name ?? entry.after?.name ?? "—";
  }

  if (entry.action === "delete") {
    return entry.before?.description ?? entry.before?.child_name ?? entry.before?.name ?? "—";
  }

  const before = entry.before ?? {};
  const after = entry.after ?? {};
  const changes = Object.keys(after)
    .filter((key) => !NOISE.has(key))
    .filter((key) => JSON.stringify(before[key]) !== JSON.stringify(after[key]))
    .map((key) => `${key}: ${format(before[key])} → ${format(after[key])}`);

  return changes.length > 0 ? changes.join(" · ") : "sin cambios visibles";
}

function format(value) {
  if (value === null || value === undefined || value === "") return "vacío";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function formatTimestamp(iso) {
  const d = new Date(iso);
  return d.toLocaleString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
